import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

// Détecte la mise en ligne d'une nouvelle version du backoffice et propose de recharger.
//
// Principe : chaque build CRA produit un bundle `static/js/main.<hash>.js` référencé par
// index.html. On compare le hash chargé par la page à celui de l'index.html servi
// actuellement (relu sans cache). S'ils diffèrent, une nouvelle version a été déployée.
//
// Vérification : toutes les `intervalMs` (5 min par défaut) et à chaque retour sur l'onglet.
// Inactif en développement (pas de bundle hashé). Ne recharge jamais de force.
const DEFAULT_INTERVAL = 5 * 60 * 1000;
const BUNDLE_RE = /static\/js\/main\.([a-f0-9]+)\.js/;
const TOAST_ID = 'nouvelle-version';

const getLoadedHash = () => {
    const scripts = Array.from(document.querySelectorAll('script[src]'));
    for (const s of scripts) {
        const m = s.getAttribute('src').match(BUNDLE_RE);
        if (m) return m[1];
    }
    return null;
};

const NouvelleVersionToast = () => (
    <div>
        <strong>Nouvelle version disponible</strong>
        <div style={{ margin: '4px 0 10px', fontSize: 13 }}>
            Le backoffice a été mis à jour. Rechargez pour profiter des dernières fonctionnalités.
        </div>
        <Button
            type="primary"
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => window.location.reload()}
        >
            Recharger
        </Button>
    </div>
);

const useVersionCheck = ({ intervalMs = DEFAULT_INTERVAL } = {}) => {
    const notified = useRef(false);

    useEffect(() => {
        const loadedHash = getLoadedHash();
        if (!loadedHash) return undefined;

        const check = async () => {
            if (notified.current || document.visibilityState !== 'visible') return;
            try {
                const res = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
                if (!res.ok) return;
                const m = (await res.text()).match(BUNDLE_RE);
                if (m && m[1] !== loadedHash) {
                    notified.current = true;
                    toast.info(<NouvelleVersionToast />, {
                        toastId: TOAST_ID,
                        autoClose: false,
                        closeOnClick: false,
                        draggable: false,
                    });
                }
            } catch { /* hors ligne : on réessaiera au prochain passage */ }
        };

        const timer = setInterval(check, intervalMs);
        document.addEventListener('visibilitychange', check);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', check);
        };
    }, [intervalMs]);
};

export default useVersionCheck;
